import React from 'react';
import PropTypes from 'prop-types';
import View from 'components/view';
import Side from 'components/view/side';
import Icon from 'components/icon';
import DateTime from 'components/date-time';
import { Form, TabView, Fieldgroup } from 'components/form';
import { Section, SectionGroup } from 'components/section';
import Button from 'components/button';
import AppIcon from './icon';
import AppLocations from './locations';
import AppStorefronts from './storefronts';
import AppPermissions from './permissions';
import AppConfigs from './configs';
import AppSettings from './settings';
import InstalledAppStatus from './installed-status';
import AsyncAppStatus from './async-status';
import VersionStatus from './version-status';
import {
  renderAppTypeLabel,
  appUpdateAvailable,
  isVersionGreater,
} from 'utils/app';
import { renderMarkdown } from 'utils/string';
import { getSettingContentModels } from 'utils/content/app';

export default class AppView extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    notifyError: PropTypes.func.isRequired,
    notifySuccess: PropTypes.func.isRequired,
  };

  onClickUpdate = (event) => {
    event.preventDefault();
    const { notifyError } = this.context;
    const { record, installedApp, onUpdateApp, router } = this.props;
    const { app } = installedApp;

    this.context.openModal('Confirm', {
      title: `Update version`,
      message: (
        <p>
          Are you sure you want to update {app.name} version{' '}
          {installedApp.version} &rarr; <b>{record.version}</b>?
        </p>
      ),
      action: 'Update',
      loadingTitle: `Updating ${app.name}`,
      width: 600,
      extraActions: [
        {
          label: 'View release notes',
          type: 'secondary',
          onClick: () => {
            this.context.closeModal();
            router.push(`/apps/${app.id}/?tab=versions`);
          },
        },
      ],
      onConfirm: () => {
        return onUpdateApp({ version: record.version }).then((result) => {
          if (result?.errors) {
            notifyError(result.errors);
          }
        });
      },
    });
  };

  onClickEnable = (event) => {
    event.preventDefault();
    const { notifyError, notifySuccess } = this.context;
    const { installedApp, onUpdateApp } = this.props;
    const { app } = installedApp;

    this.context.openModal('Confirm', {
      title: `Enable ${app.name}`,
      message: <p>This will restore all app functionality.</p>,
      action: 'Enable',
      loadingTitle: `Enabling ${app.name}`,
      width: 500,
      onConfirm: () => {
        return onUpdateApp({ active: true }).then((result) => {
          if (result?.errors) {
            notifyError(result.errors);
          } else {
            notifySuccess(`${app.name} app enabled`);
          }
        });
      },
    });
  };

  onClickDisable = (event) => {
    event.preventDefault();
    const { notifyError, notifySuccess } = this.context;
    const { installedApp, appStorefronts, onUpdateApp } = this.props;
    const { app } = installedApp;

    if (appStorefronts?.length > 0) {
      this.context.openModal('Confirm', {
        title: `Disable ${app.name}`,
        message: (
          <p>
            You may not disable this {app.type === 'theme' ? 'theme' : 'app'}{' '}
            while there are ({appStorefronts.length}) storefronts using it.
          </p>
        ),
        actionHidden: true,
        cancelText: 'Ok',
      });
      return;
    }

    this.context.openModal('Confirm', {
      title: `Disable ${app.name}`,
      message: (
        <p>
          This will disable app fields and events. App data will remain
          unaffected.
          <br />
          <br />
          You can re-enable the app at a later time.
        </p>
      ),
      action: 'Disable',
      actionType: 'danger',
      loadingTitle: `Disabling ${app.name}`,
      onConfirm: () => {
        return onUpdateApp({ active: false }).then((result) => {
          if (result?.errors) {
            notifyError(result.errors);
          } else {
            notifySuccess(`${app.name} app disabled`);
          }
        });
      },
    });
  };

  onClickUninstall = (event) => {
    event.preventDefault();
    const { installedApp, uninstallApp, deleteApp, appStorefronts, router } =
      this.props;
    const { app } = installedApp;

    this.context.openModal('UninstallDeleteApp', {
      app,
      appStorefronts,
      uninstallApp,
      deleteApp,
      onSuccess: () => router.replace(`/apps?tab=${app.type}`),
    });
  };

  getSettingContentModels() {
    const { appConfigs, installedApp } = this.props;
    const { app } = installedApp;

    const settingConfigs = appConfigs.results.filter(
      (config) => config.type === 'setting',
    );

    return settingConfigs.map((config) => ({
      ...config.values,
      collection: 'settings',
      root: config.values.name,
      appSettingsField: true,
      app,
    }));
  }

  render() {
    const {
      record,
      installedApp,
      appVersions,
      appConfigs,
      settings,
      values,
      onChangeSettings,
      onSubmitSettings,
      location,
      edited,
    } = this.props;
    const { client } = this.context;

    const { app } = installedApp;
    const updateAvailable = appUpdateAvailable(app, installedApp);

    const tab = location.query.tab || 'default';

    const developer = record.partner || record.client;
    const supportUrl = app.support_url || developer.support_website;

    const showSettings = app.type !== 'storefront';

    const settingContentModels =
      showSettings && getSettingContentModels(app, appConfigs);

    return (
      <div className="apps view-detail-full">
        <View
          detail={true}
          uri="/apps"
          sectionTitle="Apps"
          headerTitle={<>{app.name}</>}
          headerStatus={
            <span className="apps-list-status">
              <AsyncAppStatus installedApp={installedApp} />
              <InstalledAppStatus
                installedApp={installedApp}
                onClickUpdate={this.onClickUpdate}
              />
            </span>
          }
          headerSubtitle={app.description}
          headerImage={<AppIcon app={app} size={60} />}
          headerActions={[
            tab === 'settings' && {
              label: 'Save settings',
              type: edited ? 'default' : 'secondary',
              onClick: () => this.refs.form.submit(),
            },
          ]}
          menuActions={[
            ...(updateAvailable
              ? [
                  {
                    label: (
                      <span className="with-icon">
                        <Icon fa="sparkles" />
                        Update to {updateAvailable}
                      </span>
                    ),
                    onClick: this.onClickUpdate,
                    className: 'primary',
                  },
                  <hr />,
                ]
              : []),
            installedApp.active === false
              ? {
                  label: 'Enable app',
                  onClick: this.onClickEnable,
                }
              : {
                  label: 'Disable app',
                  onClick: this.onClickDisable,
                },
          ]}
          extraActions={[
            {
              label: 'Uninstall app',
              onClick: this.onClickUninstall,
              className: 'danger',
            },
          ]}
          tabs={{
            default: { label: 'Overview' },
            ...(settingContentModels?.length > 0 && {
              settings: { label: 'Settings' },
            }),
            versions: { label: 'Versions' },
          }}
        >
          <TabView value={tab} active="default">
            <fieldset className="last-child">
              <div className="view-body-section-group">
                <AppLocations {...this.props} />
                {app.type === 'theme' ? (
                  <AppStorefronts {...this.props} />
                ) : (
                  <>
                    <AppPermissions {...this.props} />
                    {app.type === 'storefront' &&
                      (!app.kind || app.kind === 'shop') && (
                        <AppStorefronts {...this.props} />
                      )}
                    {appConfigs.results.length > 0 && (
                      <Fieldgroup label="App configurations" className="normal">
                        <AppConfigs {...this.props} />
                      </Fieldgroup>
                    )}
                  </>
                )}
              </div>
            </fieldset>
          </TabView>

          {settingContentModels?.length > 0 && (
            <TabView value={tab} active="settings">
              <Form
                ref="form"
                onSubmit={onSubmitSettings}
                onChange={onChangeSettings}
                values={values}
              >
                <AppSettings
                  {...this.props}
                  record={settings}
                  values={values}
                  settingContentModels={settingContentModels}
                />
              </Form>
            </TabView>
          )}

          <TabView value={tab} active="versions">
            <fieldset className="last-child">
              <SectionGroup>
                {appVersions.count > 0 ? (
                  appVersions.results?.map((version, index) => (
                    <Section
                      title={version.version}
                      subtitle={
                        <DateTime value={version.date_created} format="short" />
                      }
                      key={version.id}
                      actions={
                        <>
                          {app.client_id === client.id && (
                            <span className="apps-list-status">
                              <VersionStatus app={app} version={version} />
                            </span>
                          )}
                          {index === 0 &&
                          app.type !== 'theme' &&
                          isVersionGreater(
                            version.version,
                            installedApp.version,
                          ) ? (
                            <Button
                              size="sm"
                              type="default"
                              onClick={this.onClickUpdate}
                            >
                              Install latest version
                            </Button>
                          ) : undefined}
                        </>
                      }
                    >
                      {version.release_notes
                        ? renderMarkdown(version.release_notes)
                        : null}
                    </Section>
                  ))
                ) : (
                  <Section
                    title="No versions available"
                    subtitle="This app has not been versioned yet."
                    key="none"
                  />
                )}
              </SectionGroup>
            </fieldset>
          </TabView>

          {(tab === 'default' || tab === 'versions') && (
            <Side>
              <div className="view-side-container">
                <section className="view-side-section">
                  <div className="view-side-section-header">
                    <h4>Type</h4>
                  </div>
                  <div className="view-side-body">
                    {renderAppTypeLabel(app.type)}
                  </div>
                </section>
                <section className="view-side-section">
                  <div className="view-side-section-header">
                    <h4>Developer</h4>
                  </div>
                  <div className="view-side-body">
                    <div>{developer.name}</div>
                  </div>
                </section>
                <section className="view-side-section">
                  <div className="view-side-section-header">
                    <h4>Installed version</h4>
                  </div>
                  <div className="view-side-body">
                    {installedApp.version || app.version}
                  </div>
                </section>
                {supportUrl && (
                  <section className="view-side-section">
                    <div className="view-side-section-header">
                      <h4>Website</h4>
                    </div>
                    <div className="view-side-body">{supportUrl}</div>
                  </section>
                )}
                {app.published_id && (
                  <section className="view-side-section">
                    <div className="view-side-section-header">
                      <h4>App ID</h4>
                    </div>
                    <div className="view-side-body">
                      <code>{app.published_id}</code>
                    </div>
                  </section>
                )}
                {(!app.public_id ||
                  installedApp.app_slug_id !== app.public_id) && (
                  <section className="view-side-section">
                    <div className="view-side-section-header">
                      <h4>Private ID</h4>
                    </div>
                    <div className="view-side-body">
                      <code>{installedApp.app_slug_id}</code>
                    </div>
                  </section>
                )}
                <section className="view-side-section">
                  <div className="view-side-section-header">
                    <h4>Installed</h4>
                  </div>
                  <div className="view-side-body">
                    <DateTime
                      value={
                        installedApp.date_installed || installedApp.date_created
                      }
                      format="age"
                    />
                  </div>
                </section>
              </div>
            </Side>
          )}
        </View>
      </div>
    );
  }
}
